/**
=========================================================
* Trust Cargo - table row actions
=========================================================

* Compact icon buttons instead of a row of text links.
*
* The text version wrapped onto two lines whenever a row had three actions,
* which left every Operations cell a different height and the words landing
* in different places down the column. Fixed-size icon buttons cannot wrap,
* so the column reads as one clean strip, and the label survives as a
* tooltip.
*/

import PropTypes from "prop-types";
import Icon from "@mui/material/Icon";
import Tooltip from "@mui/material/Tooltip";
import SuiBox from "components/SuiBox";

/** colour name -> the palette entry the button tints with */
const TONE = {
  default: "dark",
  primary: "primary",
  danger: "error",
  info: "info",
};

function RowActions({ actions }) {
  return (
    <SuiBox display="flex" alignItems="center" justifyContent="center" gap={0.75}>
      {actions.filter(Boolean).map((a) => {
        const tone = TONE[a.tone || "default"];

        return (
          <Tooltip key={a.label} title={a.label} arrow disableInteractive>
            {/* span wrapper so the tooltip still works while disabled */}
            <span>
              <SuiBox
                component="button"
                type="button"
                disabled={a.disabled}
                onClick={a.disabled ? undefined : a.onClick}
                aria-label={a.label}
                sx={({ palette, borders, functions: { rgba } }) => ({
                  width: 32,
                  height: 32,
                  display: "grid",
                  placeItems: "center",
                  cursor: a.disabled ? "not-allowed" : "pointer",
                  opacity: a.disabled ? 0.4 : 1,
                  border: `1px solid ${rgba(palette[tone].main, 0.28)}`,
                  borderRadius: borders.borderRadius.md,
                  backgroundColor: rgba(palette[tone].main, 0.07),
                  color: palette[tone].main,
                  transition: "background-color 150ms ease, border-color 150ms ease",
                  "&:hover": a.disabled
                    ? {}
                    : {
                        backgroundColor: rgba(palette[tone].main, 0.18),
                        borderColor: rgba(palette[tone].main, 0.5),
                      },
                })}
              >
                <Icon sx={{ fontSize: "18px !important" }}>{a.icon}</Icon>
              </SuiBox>
            </span>
          </Tooltip>
        );
      })}
    </SuiBox>
  );
}

RowActions.propTypes = {
  actions: PropTypes.arrayOf(
    PropTypes.shape({
      icon: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      onClick: PropTypes.func,
      tone: PropTypes.oneOf(["default", "primary", "danger", "info"]),
      disabled: PropTypes.bool,
    })
  ).isRequired,
};

export default RowActions;
